import React, { useState, useEffect, useImperativeHandle, useRef } from 'react';
import classnames from 'classnames';
import { history } from 'umi';
import { handleExtendTimeFilterValue, formatFilterSaveValue } from '@intelligence/utils/filterUtils';
import { Card } from 'tea-component';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import { GENRE_SOURCE_MAP, GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import { handleSensorTowerValueFilter } from '@intelligence/utils/topchartsUtils';

import {
  // 时间筛选器
  RANGE_TIME as FILTER_RANGE_TIME,
  // 单平台筛选器
  SINGLE_PLATFORM_SELECT as FILTER_SINGLE_PLATFORM_SELECT,
  // 组合国家筛选器
  COMPOSED_COUNTRY_SELECT as FILTER_COMPOSED_COUNTRY_SELECT,
  // 品类筛选器
  GENRETOR as FILTER_GENRE_SELECT,
  // Product Model筛选器
  SENSOR_TOWER_TAG_PRODUCT_MODEL,
  // 热门标签筛选器
  HOT_TAGS_SOURCE,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FILTER_DEFAULT_INPUT_LIST, FILTER_DEFAULT_VALUE, MOBILE_STORE_RANK_COUNTRY_INHERIT_CHANGED } from './_config';

import './index.scss';

import type { IBaseProps, IRegion } from '@/pages/intelligence/types/common';
import type { IMobileStoreRankTopFilter, IStoreRankTopFilter } from '@/pages/intelligence/types/storeRank';
import type { IFilterInstance } from '@/pages/intelligence/components/Filter';
import useFilterUseHook from '@/pages/intelligence/hooks/useFilterUseHook';

/**
 * AllTopChartsFilter 组件实例
 */
export interface IAllTopChartsFilterInstance extends IFilterInstance {
  setFormValue: (payload: any, options?: { resetToDefault?: boolean }) => void;
}

interface IMobileStoreRankTopFilterProps extends IBaseProps {
  isOperate: boolean;
  regionOptions: IRegion[] | null;
  multiCountryText?: React.ReactNode;
  onClickMultiCountry?: () => void;
  filter: IStoreRankTopFilter | IMobileStoreRankTopFilter;
  onConfirm: (payload: IMobileStoreRankTopFilter) => void;
  onReset: () => void;
  rangeTimeComponentDefault: any;
  /** 热门标签选项列表 */
  hotTagsOptions?: any[];
  /** URL filter_tag_name 参数，用于确定 HotTagsSource 默认选中项 */
  filterTagName?: string;
  /** HotTagsSource 选项变化时的回调 */
  onHotTagConfirm?: (option: any) => void;
}

const MobileStoreRankTopFilter: React.ForwardRefRenderFunction<IAllTopChartsFilterInstance, IMobileStoreRankTopFilterProps> = (props, ref) => {
  const { style, className, isOperate, regionOptions, multiCountryText, onClickMultiCountry, filter, onConfirm, onReset, rangeTimeComponentDefault, entityType, hotTagsOptions, filterTagName, onHotTagConfirm } = props;

  const { filterExtend: filterExtendTmp, setFilterExtend, resetFilterExtend } = useFilterExtend({});
  const { tagConfig } = useSenTowerTag({});
  const countryChangedRef = useRef(false);
  const urlStore = history.location.query?.store as string;

  const initialFormValue = (() => {
    if (!isOperate) {
      return {
        ...FILTER_DEFAULT_VALUE,
        [FILTER_SINGLE_PLATFORM_SELECT]: {
          value: urlStore || FILTER_DEFAULT_VALUE[FILTER_SINGLE_PLATFORM_SELECT]?.value,
        },
      };
    }
    const filterExtend = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtendTmp);
    const rangeTimeFilterExtend = filterExtend[FILTER_RANGE_TIME];
    const singlePlatformSelectFilterExtend = filterExtend[FILTER_SINGLE_PLATFORM_SELECT];
    const composedCountrySelectFilterExtend = filterExtend[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    const genreSelectFilterExtend = filterExtend[FILTER_GENRE_SELECT];
    const productModelFilterExtend = filterExtend[SENSOR_TOWER_TAG_PRODUCT_MODEL];

    const rangeTimeDefault = FILTER_DEFAULT_VALUE[FILTER_RANGE_TIME];
    const singlePlatformSelectDefault = FILTER_DEFAULT_VALUE[FILTER_SINGLE_PLATFORM_SELECT];
    const composedCountrySelectDefault = FILTER_DEFAULT_VALUE[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    const genreSelectDefault = FILTER_DEFAULT_VALUE[FILTER_GENRE_SELECT];
    const productModelDefault = FILTER_DEFAULT_VALUE[SENSOR_TOWER_TAG_PRODUCT_MODEL];

    return {
      [FILTER_RANGE_TIME]: {
        type: 'daily', // rangeTimeFilterExtend?.filterType ?? rangeTimeDefault?.type,
        value: handleExtendTimeFilterValue({
          // 仅有天粒度，单选1天
          needSameDay: true,
          extendFilterValues: rangeTimeFilterExtend,
          defaultValue: rangeTimeDefault?.value,
        }),
        componentDefault: rangeTimeComponentDefault,
      },
      [FILTER_SINGLE_PLATFORM_SELECT]: {
        value: urlStore || singlePlatformSelectFilterExtend?.value || singlePlatformSelectDefault?.value,
      },
      [FILTER_COMPOSED_COUNTRY_SELECT]: {
        value: {
          // type: composedCountrySelectFilterExtend?.type ?? composedCountrySelectDefault?.type,
          type: 'single',
          countries:
            composedCountrySelectFilterExtend?.countries?.length === 1
              ? composedCountrySelectFilterExtend?.countries
              : composedCountrySelectDefault?.countries,
        },
      },
      [FILTER_GENRE_SELECT]: {
        source: genreSelectFilterExtend?.genretor?.source ?? genreSelectDefault?.source,
        mainGenres: genreSelectFilterExtend?.genretor?.mainGenres ?? genreSelectDefault?.mainGenres,
        subGenres: genreSelectFilterExtend?.genretor?.subGenres ?? genreSelectDefault?.subGenres,
        level3Genres: genreSelectFilterExtend?.genretor?.level3Genres ?? genreSelectDefault?.level3Genres,
        level4Genres: genreSelectFilterExtend?.genretor?.level4Genres ?? genreSelectDefault?.level4Genres,
      },
      [SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
        value: productModelFilterExtend?.value ?? productModelDefault?.value ?? [],
      },
    };
  })();

  const {
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    level3Genres,
    level4Genres,

    handleGenresChange,
    handleReset: handleGenresReset,
    // getGenresReqParams,
    // updateGenres2Store,
  } = useGenreComponent(props, undefined, { defaultGenreSource: GENRE_SOURCE_MAP.APP_MAGIC });

  const [inputList, setInputList] = useState(FILTER_DEFAULT_INPUT_LIST);

  // const formValue = useRef<any>(initialFormValue);
  const [formValue, setFormValue] = useState(initialFormValue);

  const formatFormValue = (values: any) => {
    const newFilterValues = formatFilterSaveValue({
      values,
      timeConfig: {
        // 天粒度单选1天
        needSameDay: true,
      },
      selectRegionConfig: {
        // 单选的国家筛选器
        isSingleCountry: true,
      },
    });
    return newFilterValues;
  };

  const {
    filterName,
    filterData,
    updateFormValue,
    onSaveFilterMsg,
    onFilterChange,
  } = useFilterUseHook({
    defaultValue: FILTER_DEFAULT_VALUE,
    formValue,
    setFormValue,
    timeFilterValues: {
      type: 'daily',
      value: [filter?.start_date, filter?.end_date]
    },
    formatFormValue,
    onChangeExtraLast: (key: string) => {
      if (key === FILTER_COMPOSED_COUNTRY_SELECT) {
        countryChangedRef.current = true;
      }
    },
  });


  const handleConfirm = (value: any, apiValue: any) => {
    setFormValue(value);
    const nextApiValue = { ...apiValue };
    if (countryChangedRef.current) {
      nextApiValue[MOBILE_STORE_RANK_COUNTRY_INHERIT_CHANGED] = {
        value: true,
      };
    }
    // 获取 Sensor Tower Tag 筛选器数据并转换为接口入参格式
    const sensorTowerTagParams = handleSensorTowerValueFilter(apiValue);

    const filterForm = {
      store: apiValue[FILTER_SINGLE_PLATFORM_SELECT]?.value,
      regions: apiValue[FILTER_COMPOSED_COUNTRY_SELECT]?.value?.countries,
      start_date: apiValue[FILTER_RANGE_TIME]?.start_date,
      end_date: apiValue[FILTER_RANGE_TIME]?.end_date,
      genre: apiValue[FILTER_GENRE_SELECT]?.genre,
      sub_genre: apiValue[FILTER_GENRE_SELECT]?.sub_genre,
      genre_source: apiValue[FILTER_GENRE_SELECT]?.genre_source,
      iegg_genre_req: apiValue[FILTER_GENRE_SELECT]?.iegg_genre_req,
      sensortower_tag_filter: sensorTowerTagParams?.sensortower_tag_filter || [],
      // 传递原始品类数据，供 storeRank.ts 统一处理 filter 对象
      appannie_genre: apiValue[FILTER_GENRE_SELECT]?.appannie_genre || [],
      sensortower_genre: apiValue[FILTER_GENRE_SELECT]?.sensortower_genre || [],
      iegg_genre: apiValue[FILTER_GENRE_SELECT]?.iegg_genre || [],
      appmagic_genre: apiValue[FILTER_GENRE_SELECT]?.appmagic_genre || [],
      [SENSOR_TOWER_TAG_PRODUCT_MODEL]: apiValue[SENSOR_TOWER_TAG_PRODUCT_MODEL],
    };
    onConfirm?.(filterForm);
    // 点击确认后，将筛选器保存方案及保存数据存到缓存中
    onSaveFilterMsg(value, nextApiValue);
    // 榜单国家筛选器在confirm过滤继承值后需要更新继承值
    // feat国家筛选闪动问题,setFilterExtend放在后面,
    setFilterExtend(nextApiValue);
  };

  const handleReset = () => {
    countryChangedRef.current = false;
    const newFormValue = {
      ...FILTER_DEFAULT_VALUE,
      [FILTER_RANGE_TIME]: {
        type: 'date',
        value: rangeTimeComponentDefault?.date
      },
    };
    setFormValue(newFormValue);
    genreRef.current?.reset();
    resetFilterExtend();
    onReset?.();

    // 如果有默认 HotTag，reset 完成后重新触发 HotTag 选中
    // 走统一的下拉选项逻辑，恢复默认 HotTag 及其对应的筛选条件
    if (filterTagName && onHotTagConfirm && Array.isArray(hotTagsOptions)) {
      const defaultHotTagOption = hotTagsOptions.find((opt: any) => opt?.name === filterTagName);
      if (defaultHotTagOption) {
        onHotTagConfirm(defaultHotTagOption);
      }
    }
  };

  /** 时间筛选器值由父组件更新 */
  useEffect(() => {
    updateFormValue(urlStore ? {
      [FILTER_SINGLE_PLATFORM_SELECT]: { value: urlStore },
    } : undefined);
  }, [
    filterName,
    JSON.stringify(filterData),
    filter?.start_date, filter?.end_date,
  ]);

  /** 监听 filter prop 中的 Product Model 变化，更新筛选器默认值 */
  useEffect(() => {
    const productModelFromFilter = (filter as any)?.[SENSOR_TOWER_TAG_PRODUCT_MODEL];
    if (productModelFromFilter?.value?.length > 0) {
      setFormValue((prev: any) => ({
        ...prev,
        [SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
          value: productModelFromFilter.value,
        },
      }));
    }
  }, [(filter as any)?.[SENSOR_TOWER_TAG_PRODUCT_MODEL]]);
  // useEffect(() => {
  //   formValue.current[FILTER_RANGE_TIME] = {
  //     ...formValue.current[FILTER_RANGE_TIME],
  //     value: [moment(filter?.start_date), moment(filter?.end_date)],
  //   };
  // }, [filter?.start_date, filter?.end_date]);

  useEffect(() => {
    const inputListTmp = [...inputList];
    const composedCountrySelectTmp = inputListTmp.find((item) => item?.key === FILTER_COMPOSED_COUNTRY_SELECT);
    if (composedCountrySelectTmp) {
      const composedCountrySelectConfig = composedCountrySelectTmp as any;
      composedCountrySelectConfig.options = regionOptions ?? [];
      composedCountrySelectConfig.multiCountryText = multiCountryText;
      composedCountrySelectConfig.onClickMultiCountry = onClickMultiCountry;
      composedCountrySelectConfig.overlayClassName = 'dbi-alltopcharts-region-cascader-select-overlay';
    }
    const genreSelectTmp = inputListTmp.find((item) => item?.key === FILTER_GENRE_SELECT);
    if (genreSelectTmp) {
      // @ts-ignore
      genreSelectTmp.genreData = {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        level3Genres,
        level4Genres,
        handleGenresChange,
        handleGenresReset,
      };
      // @ts-ignore
      genreSelectTmp.entityType = entityType;
      // @ts-ignore
      genreSelectTmp.sources = GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE;
      // @ts-ignore 启用标准化品类参数处理，使 apiValue 包含 appmagic_genre 等字段
      genreSelectTmp.isStdGenreValuesHandler = true;
    } else {
      inputListTmp.push({
        key: FILTER_GENRE_SELECT,
        // @ts-ignore
        genreData: {
          genreRef,
          genreSource,
          mainGenres,
          subGenres,
          level3Genres,
          level4Genres,
          handleGenresChange,
          handleGenresReset,
        },
        entityType,
        sources: GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE,
        // 启用标准化品类参数处理，使 apiValue 包含 appmagic_genre 等字段
        isStdGenreValuesHandler: true,
      });
    }
    // 配置 Product Model 筛选器的 options
    const productModelTmp = inputListTmp.find((item) => item?.key === SENSOR_TOWER_TAG_PRODUCT_MODEL);
    if (productModelTmp) {
      // @ts-ignore
      productModelTmp.options = tagConfig?.[SENSOR_TOWER_TAG_PRODUCT_MODEL] || [];
    }
    // 更新 HotTagsSource 筛选器的 options、默认选中项和 onHotTagConfirm
    const hotTagsSourceTmp = inputListTmp.find((item) => item?.key === HOT_TAGS_SOURCE);
    if (hotTagsSourceTmp) {
      // @ts-ignore
      hotTagsSourceTmp.options = hotTagsOptions || [];
      // @ts-ignore - 根据 filterTagName 匹配默认选中项
      hotTagsSourceTmp.hotTagDefaultValue = filterTagName
        ? (hotTagsOptions || []).find((opt: any) => opt?.name === filterTagName)
        : undefined;
      // @ts-ignore
      hotTagsSourceTmp.onHotTagConfirm = onHotTagConfirm;
    }
    setInputList(inputListTmp);
  }, [regionOptions, genreRef, genreSource, mainGenres, subGenres, level3Genres, level4Genres, multiCountryText, onClickMultiCountry, handleGenresChange, handleGenresReset, tagConfig, hotTagsOptions, filterTagName, onHotTagConfirm]);

  const filterRef = React.useRef<IFilterInstance | null>(null);

  useImperativeHandle(ref, () => ({
    setFormValue: (payload: any, options?: { resetToDefault?: boolean }) => {
      setFormValue(options?.resetToDefault ? { ...FILTER_DEFAULT_VALUE, ...payload } : { ...formValue, ...payload });
    },
    setValueAndConfirm: (partialValue: IRecord, options?: { resetToDefault?: boolean }) => {
      filterRef.current?.setValueAndConfirm(partialValue, options);
    },
  }));

  return (
    <Card style={style} className={classnames('dbi-atcfilter', className)}>
      <Card.Body>
        <Filter
          ref={filterRef}
          showButton={true}
          inputList={inputList}
          defaultValue={{
            ...FILTER_DEFAULT_VALUE,
            [FILTER_RANGE_TIME]: {
              type: 'date',
              value: rangeTimeComponentDefault?.date
            },
          }}
          value={formValue}
          onConfirm={handleConfirm}
          onReset={handleReset}
          onChange={onFilterChange}
          isHaveFilterSave={true}
        />
      </Card.Body>
    </Card>
  );
};

// @ts-ignore
export default React.forwardRef(MobileStoreRankTopFilter);
